/**
 * Login por e-mail/senha + social (Google condicional a REACT_APP_GOOGLE_CLIENT_ID). O query param ?expirada=1 (setado pelo interceptor do api.ts) exibe o aviso de sessão expirada.
 */
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { LoginSocialFacebook } from 'reactjs-social-login';
import { FaFacebookF } from 'react-icons/fa';

import { FormGroup, Input, Label } from 'ui/input';

import logoImg from 'assets/logos/logo.png';
import googleImg from 'assets/logos/social-auth/google.png';
import analisysImg from 'assets/images/login-1.jpg';

import { alert } from 'lib/alert';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import * as selectUser from 'store/user/user.selector';
import { userRefresh } from 'store/user/user.slice';

import { signIn, socialSignIn } from 'store/user/user.action';

import { ERoutes } from 'infra/routes/routes.enum';
import { FormError } from 'entities/Errors';

import {
  LoginFooter,
  RightContainer,
  LogoImg,
  AnalystsImg,
  TextContainer,
  Link,
  LoginContainer,
  LeftContainer,
  Header,
  Button,
  SocialSignInContainer,
  SocialLogoImg,
  GoogleButton,
  SmallLink,
  ForgotPasswordContainer,
  SocialOrContainer,
  FacebookButton,
  MainContainer,
} from './signin.styles';

const GoogleLoginButton = () => {
  const dispatch = useAppDispatch();
  const handleGoogleSignIn = useGoogleLogin({
    onSuccess: (response) => {
      dispatch(
        socialSignIn({ provider: 'google', token: response.access_token })
      );
    },
    onError: () =>
      alert.error(
        'Ocorreu um erro ao tentar entrar. Tente novamente ou entre em contato com o suporte.'
      ),
  });
  return (
    <GoogleButton type="button" onClick={() => handleGoogleSignIn()}>
      <div>
        <SocialLogoImg src={googleImg} alt="Google" />
      </div>
      <div>Entrar com Google</div>
    </GoogleButton>
  );
};

interface FormProps {
  email: string;
  password: string;
}

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [formValue, setFormValue] = useState<FormProps>({} as FormProps);
  const [formError, setFormError] = useState<FormError>({} as FormError);

  // Aviso de sessão expirada (redirect do interceptor em services/api.ts)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expirada') === '1') {
      alert.warning('Sua sessão expirou. Entre novamente para continuar.');
      window.history.replaceState({}, '', '/entrar');
    }
  }, []);

  const { email, password } = formValue;

  const userStatus = useAppSelector(selectUser.status);
  const userAction = useAppSelector(selectUser.action);
  const userError = useAppSelector(selectUser.error);

  useEffect(() => {
    dispatch(userRefresh());
    refreshForm();
    setFormError({} as FormError);
  }, []);

  useEffect(() => {
    if (userAction === 'signIn') {
      if (userStatus === 'successed') {
        navigate('/dashboard');
      } else if (userStatus === 'failed' && userError) {
        if (userError.type !== 'validation') {
          alert.error(userError.message);
        } else {
          const validationErrors = userError?.details?.reduce((acc, error) => {
            return { ...acc, [error.key]: error.message };
          }, {} as FormError);

          setFormError(validationErrors);
        }
      }
    }
  }, [userStatus]);

  const refreshForm = () => {
    setFormValue({
      email: '',
      password: '',
    });
    setFormError({} as FormError);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValue({ ...formValue, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(signIn({ ...formValue }));
    setFormError({} as FormError);
  };

  const handleFacebookSingIn = (response: any) => {
    dispatch(
      socialSignIn({
        provider: 'facebook',
        token: response.data.accessToken,
      })
    );
  };

  return (
    <MainContainer>
      <LoginContainer>
        <LeftContainer>
          <Header>
            <LogoImg src={logoImg} />
            <h2>Acesse sua conta</h2>
          </Header>
          <form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <FormGroup hasError={formError.email ? true : false}>
                  <Label htmlFor="signin-email">E-mail</Label>
                  <Input
                    id="signin-email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={handleChange}
                  />
                  {formError.email && <span>{formError.email}</span>}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup hasError={formError.password ? true : false}>
                  <Label htmlFor="signin-password">Senha</Label>
                  <Input
                    id="signin-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={handleChange}
                  />
                  {formError.password && <span>{formError.password}</span>}
                </FormGroup>
              </Col>
              <ForgotPasswordContainer>
                <SmallLink to={ERoutes.RecoverPassword}>
                  Esqueci a senha
                </SmallLink>
              </ForgotPasswordContainer>
            </Row>
            <Button>Entrar</Button>
          </form>
          <SocialSignInContainer>
            <SocialOrContainer>ou</SocialOrContainer>
            {process.env.REACT_APP_GOOGLE_CLIENT_ID && <GoogleLoginButton />}
            <LoginSocialFacebook
              appId={process.env.REACT_APP_FACEBOOK_APP_ID || ''}
              onResolve={handleFacebookSingIn}
              onReject={() => {
                alert.error(
                  'Ocorreu um erro ao tentar criar conta. Tente novamente ou entre em contato com o suporte.'
                );
              }}
            >
              <FacebookButton type="button">
                <div>
                  <FaFacebookF size={20} color="#fff" />
                </div>
                <div>Entrar com Facebook</div>
              </FacebookButton>
            </LoginSocialFacebook>
          </SocialSignInContainer>
          <LoginFooter>
            <span>
              Ainda não tem uma conta? <Link to="/cadastrar">Crie uma!</Link>
            </span>
          </LoginFooter>
        </LeftContainer>
        <RightContainer>
          <TextContainer>
            <h2>
              Você sabia que, por trás do EnergyPass, trabalha um time de
              especialistas em geração de energia para que você tenha a melhor
              economia?
            </h2>
          </TextContainer>
          <AnalystsImg src={analisysImg} />
        </RightContainer>
      </LoginContainer>
    </MainContainer>
  );
};

export default SignIn;
